import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  // Typography,
  Divider,
  Button,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { spacing } from "@mui/system";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import apiUtils from "../../utils/apiUtils";
import { getThemeTextColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import ViewCheckAutomatedInvoiceDialog from "../../components/ViewCheckAutomatedInvoiceDialog";
import UploadStatusModal from "../../components/UploadStatusModal";
import SelectWrapper from "../../components/SelectBoxDataGridCell";
import moment from "moment";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { GlobalContext } from "../../contexts/GlobalContext";

// Styled Components
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

// Define errorCodesList outside the component to make it stable
const errorCodesList = [
  // Hard Errors
  { code: 0, description: "Experiment mislukt.", type: "hard" },
  { code: 1, description: "Experiment geslaagd.", type: "hard" },
  { code: 3, description: "Er is een error opgetreden.", type: "hard" },
  { code: 4, description: "Error bij het extraheren van JSON.", type: "hard" },
  { code: 5, description: "Schrijven naar database mislukt.", type: "hard" },
  { code: 6, description: "Onbekende error.", type: "hard" },
  {
    code: 10,
    description:
      "Het ophalen van een bedrag (float) tijdens de validatie-experimenten is mislukt. Waarschijnlijk een parsingfout. (OCR-fout bijvoorbeeld)",
    type: "hard",
  },
  {
    code: 22,
    description:
      "Vergelijking van bedragen is groter dan de toegestane marge of fout, maar het verschil tussen de waarden is kleiner dan 2 keer de marge. Waarschijnlijk een afrondingsfout.",
    type: "hard",
  },
  {
    code: 25,
    description:
      "De vergelijking van de bedragen is groter dan de toegestane marge of fout, maar het verschil tussen de waarden is kleiner dan 5 keer de marge. Waarschijnlijk een afrondingsfout.",
    type: "hard",
  },
  {
    code: 210,
    description:
      "De vergelijking van de bedragen is groter dan de toegestane marge of fout, maar het verschil tussen de waarden is kleiner dan 10 keer de marge. Waarschijnlijk een afrondingsfout.",
    type: "hard",
  },
  // Soft Errors have been removed from errorCodesList
];

// Define soft error codes and their descriptions
const softErrorDescriptionsMap = {
  // 612: "Invoice is older than 12 months.",
  621: "Invoice is older than 21 months.",
  // 63: "Invoice is between 3 and 6 months old.",
  // 66: "Invoice is between 6 and 12 months old.",
  50: "Supplier VAT ID not found.",
  55: "Suppliers country code is the same as the customers country code.",
  70: "Gasoline found on the invoice.",
  80: "Unknown customer",
  81: "Entity type other then customer",
  90: "No product/Unknown product",
  9: "VAT amount does not match the transaction data. (Currently only for MSTS)",
  22: "Comparison of amounts is greater than the allowed margin or error, but the difference between the values is less than 2 times the margin. Probably a rounding error.",
  25: "Comparison of amounts is greater than the allowed margin or error, but the difference between the values is less than 5 times the margin. Probably a rounding error.",
  210: "Comparison of amounts is greater than the allowed margin or error, but the difference between the values is less than 10 times the margin. Probably a rounding error.",
  220: "MSTS Denmark and Sweden",

  700: "Missing required field: invoiceId",
  701: "Missing required field: invoiceNumber",
  // 702: "Missing required field: taxIdSupplier",
  // 703: "Missing required field: taxIdCustomer",
  704: "Missing required field: countryCodeCustomer",
  705: "Missing required field: countryCodeSupplier",
  706: "Missing required field: customerNumber",
  // 707: "Invalid productId (must be greater than 0)",
  708: "Missing required field: Invoice net amount",
  709: "Missing required field: Invoice VAT amount",
  710: "Invalid invoiceDate",
};

// Utility function to compute soft errors for a given invoice
const computeSoftErrorsForInvoice = (invoice, countryData, supplierData, entitiesData, productData) => {
  // console.log(invoice);
  const softErrors = [];

  if (!invoice.invoiceId) softErrors.push(700);
  if (!invoice.invoiceNumber) softErrors.push(701);
  // if (!invoice.taxIdSupplier) softErrors.push(702);
  // if (!invoice.taxIdCustomer) softErrors.push(703);
  if (!invoice.countryCodeCustomer) softErrors.push(704);
  if (!invoice.countryCodeSupplier) softErrors.push(705);
  if (!invoice.customerNumber) softErrors.push(706);
  // if (Number(invoice.productId) <= 0) softErrors.push(707);
  if (!invoice.totalExcludingTax) softErrors.push(708);
  if (!invoice.totalTax) softErrors.push(709);
  const date = new Date(invoice.invoiceDate);
  if (
    !invoice.invoiceDate ||
    isNaN(date.getTime()) ||
    invoice.invoiceDate === "0000-00-00" ||
    invoice.invoiceDate === "01-01-1970" ||
    date.getFullYear() === 1970
  ) {
    softErrors.push(710);
  }

  const now = new Date();
  const invoiceDate = new Date(invoice.invoiceDate);
  const diffInMonths = (now.getFullYear() - invoiceDate.getFullYear()) * 12 + (now.getMonth() - invoiceDate.getMonth());

  // console.log("Entities:", entitiesData);

  // Age-based soft errors
  // if (diffInMonths >= 12 && diffInMonths < 21) {
  //   softErrors.push(612); // Invoice older than 12 months
  // }
  // if (diffInMonths >= 3 && diffInMonths < 6) {
  //   softErrors.push(63); // Invoice between 3 and 6 months old
  // }
  // if (diffInMonths >= 6 && diffInMonths < 12) {
  //   softErrors.push(66); // Invoice between 6 and 12 months old
  // }
  if (diffInMonths >= 21) {
    softErrors.push(621); // Invoice older than 21 months
  }

  // Supplier VAT ID validation
  if (invoice.taxIdSupplier) {
    const countryCode = countryData.find((country) => invoice.taxIdSupplier.startsWith(country.countryCode));
    const vatIdToCheck = countryCode ? invoice.taxIdSupplier.slice(countryCode.countryCode.length) : invoice.taxIdSupplier;
    if (!supplierData.find((supplier) => supplier.supplierVATnr === vatIdToCheck)) {
      softErrors.push(50); // Supplier VAT ID not found
    }
  }

  // Country code comparison
  if (invoice.countryCodeCustomer === invoice.countryCodeSupplier) {
    softErrors.push(55); // Suppliers country code is the same as the customer's country code
  }

  // Benzine encounter validation
  if (invoice.encounteredBenzine && (invoice.countryCodeSupplier === "BE" || invoice.countryCodeSupplier === "PL")) {
    softErrors.push(70); // Error if encountered Benzine is true and countryCodeSupplier is NL or PL
  }

  // Customer validation
  const customer = entitiesData.find((user) => user.vat === invoice.taxIdCustomer && user.typeRelation === 1);
  if (!customer) {
    softErrors.push(80); // Unknown customer
  } else if (customer.typeRelation !== 1) {
    softErrors.push(81); // Entity type other than customer
  }

  // Product validation
  if (!invoice.productId || !productData.some((product) => product.productTypeId === Number(invoice.productId))) {
    softErrors.push(90); // Error if productId is null, empty, or not found in productData
  }

  // Supplier-specific soft errors
  const foundSupplier = supplierData.find((supplier) => {
    if (invoice.taxIdSupplier) {
      const countryCode = countryData.find((country) => country.countryCode && invoice.taxIdSupplier.startsWith(country.countryCode));
      const vatIdToCheck = countryCode ? invoice.taxIdSupplier.slice(countryCode.countryCode.length) : invoice.taxIdSupplier;
      return supplier.supplierVATnr === vatIdToCheck;
    }
    return false;
  });

  if (foundSupplier) {
    if (foundSupplier.supplierCode === 2111 || foundSupplier.supplierCode === 2113) {
      softErrors.push(220); // Push 220 if supplierCode matches
    }
  }

  return softErrors;
};

const InvoiceOverviewComponent = () => {
  const theme = useTheme();
  const { globalData } = useContext(GlobalContext);
  const uploadedBy = globalData?.userId;

  // State Variables
  const pageSize = 15;
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0 for 'All Invoices' tab
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("any");
  const [selectedUser, setSelectedUser] = useState("any");
  const [selectedYear, setSelectedYear] = useState("any");
  const [selectedStartMonth, setSelectedStartMonth] = useState("any");
  const [selectedEndMonth, setSelectedEndMonth] = useState("any");
  const [selectedState, setSelectedState] = useState("any");
  const [fetchedInvoiceData, setFetchedInvoiceData] = useState([]); // Data fetched from server
  const [localInvoiceUpdates, setLocalInvoiceUpdates] = useState({}); // Local updates keyed by id
  const [openAlert, setOpenAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [rightPaneSize, setRightPaneSize] = useState(0);
  const [lastRightPaneSize, setLastRightPaneSize] = useState("50%");
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    downloadFile: true,
    params: {},
  });
  const containerRef = useRef(null);
  const [selectedBatchId, setSelectedBatchId] = useState("any");
  const [batchIdOptions, setBatchIdOptions] = useState([]);
  // const [errorCodeOptions, setErrorCodeOptions] = useState([]);

  // New State Variables for Upload
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadInProgress = false; // Placeholder for actual upload status
  const [uploadResults, setUploadResults] = useState([]); // Optional: To store individual upload results

  // Existing data fetchers
  const supplierData = apiUtils.useApiData("/api/v1/suppliers");
  const currencyData = apiUtils.useApiData("/api/v1/currencies");
  const productData = apiUtils.useApiData("/api/v1/productTypes");
  const entitiesData = apiUtils.useApiData("/api/v1/entities");
  const dwsUserData = apiUtils.useApiData("/api/v1/GetDWSUsers");
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const invoiceStateData = apiUtils.useApiData("/api/v1/invoiceStates");
  // const [selectedErrorCodes, setSelectedErrorCodes] = useState([]);

  // const [deleteInProgress, setDeleteInProgress] = useState(false);
  // const [deleteResults, setDeleteResults] = useState([]);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Memoize hardErrorCodes
  const hardErrorCodes = useMemo(() => errorCodesList.filter((error) => error.type === "hard").map((error) => error.code), []);

  // Define allowed soft error codes
  // const allowedSoftErrorCodes = useMemo(() => [612, 63, 66, 70], []);
  const allowedSoftErrorCodes = useMemo(() => [70], []);
  const [selectionEnabled, setSelectionEnabled] = useState(false);

  // Compute allInvoiceData by merging fetched data and local updates
  const allInvoiceData = useMemo(() => {
    console.log("allInvoiceData - fetchedInvoiceData:", fetchedInvoiceData);
    console.log("allInvoiceData - localInvoiceUpdates:", localInvoiceUpdates);

    return fetchedInvoiceData.map((invoice) => {
      if (localInvoiceUpdates[invoice.id]) {
        console.log("allInvoiceData - updating invoice:", invoice.id);
        const updatedInvoice = { ...invoice, ...localInvoiceUpdates[invoice.id] };
        console.log("allInvoiceData - updated invoice:", updatedInvoice);
        return updatedInvoice;
      }
      return invoice;
    });
  }, [fetchedInvoiceData, localInvoiceUpdates]);

  // Compute filteredData based on current filters and active tab
  // Bereken eerst de basisdata op basis van niet-tab filters
  // Functie om de data te filteren op basis van de actieve tab
  const gridFilter = useCallback((data, tab) => {
    switch (tab) {
      case 0:
        // All Invoices: succeeded === true en geen hard errors
        return data.filter((inv) => inv.succeeded === true && (inv.errorCodes || []).length === 0);
      case 1:
        // Passed Invoices: succeeded === true, geen hard én geen soft errors
        return data.filter(
          (inv) => inv.succeeded === true && (inv.errorCodes || []).length === 0 && (inv.softErrors || []).length === 0
        );
      case 2:
        // Soft Error Invoices: succeeded === true, geen hard errors, maar wel soft errors
        return data.filter(
          (inv) => inv.succeeded === true && (inv.errorCodes || []).length === 0 && (inv.softErrors || []).length > 0
        );
      case 3:
        // Hard Error Invoices: ten minste één hard error
        return data.filter((inv) => inv.succeeded === false);
      default:
        return data;
    }
  }, []);

  // Eerst de basisdata op basis van niet-tab filters
  const baseData = useMemo(() => {
    let data = allInvoiceData;
    if (selectedCountry !== "any") {
      data = data.filter((inv) => inv.countryCodeCustomer === selectedCountry);
    }
    if (selectedUser !== "any") {
      data = data.filter((inv) => inv.uploadedBy === selectedUser);
    }
    if (selectedBatchId !== "any") {
      data = data.filter((inv) => inv.batchId === selectedBatchId);
    }
    if (selectedYear !== "any") {
      const selectedYearInt = parseInt(selectedYear, 10);
      data = data.filter((inv) => new Date(inv.invoiceDate).getFullYear() === selectedYearInt);
    }
    if (selectedStartMonth !== "any") {
      const startMonthInt = parseInt(selectedStartMonth, 10);
      data = data.filter((inv) => new Date(inv.invoiceDate).getMonth() + 1 >= startMonthInt);
    }
    if (selectedEndMonth !== "any") {
      const endMonthInt = parseInt(selectedEndMonth, 10);
      data = data.filter((inv) => new Date(inv.invoiceDate).getMonth() + 1 <= endMonthInt);
    }
    if (selectedState !== "any") {
      data = data.filter((inv) => inv.invoiceStateId === selectedState);
    }
    return data;
  }, [
    allInvoiceData,
    selectedCountry,
    selectedUser,
    selectedBatchId,
    selectedYear,
    selectedStartMonth,
    selectedEndMonth,
    selectedState,
  ]);

  // Check if all required data is loaded
  const isDataReady = useMemo(() => {
    return (
      countryData &&
      countryData.length > 0 &&
      supplierData &&
      supplierData.length > 0 &&
      entitiesData &&
      entitiesData.length > 0 &&
      productData &&
      productData.length > 0
    );
  }, [countryData, supplierData, entitiesData, productData]);

  // Check if invoices have been processed (have softErrors property)
  // This ensures that soft errors are calculated before showing the grid
  const isDataProcessed = useMemo(() => {
    // If we have no data yet, we're still waiting (show loading)
    if (fetchedInvoiceData.length === 0 && loading) {
      return false; // Still loading, show spinner
    }
    // If we have data but no invoices, that's fine (empty state)
    if (fetchedInvoiceData.length === 0 && !loading) {
      return true; // No data to show, but processing is complete
    }
    // Check if at least one invoice has softErrors property (indicating processing is complete)
    // All invoices should have softErrors after processing, so check the first one
    return fetchedInvoiceData.length > 0 && fetchedInvoiceData[0].hasOwnProperty("softErrors");
  }, [fetchedInvoiceData, loading]);

  // Grid data voor de actieve tab
  const filteredData = useMemo(() => {
    // Only return filtered data if all required data is loaded
    if (!isDataReady) {
      return [];
    }
    return gridFilter(baseData, activeTab);
  }, [baseData, activeTab, gridFilter, isDataReady]);

  // Tab counts op basis van dezelfde filtering
  const tabCounts = useMemo(() => {
    // Only calculate counts if all required data is loaded
    if (!isDataReady) {
      return {
        all: 0,
        passed: 0,
        softError: 0,
        hardError: 0,
      };
    }
    return {
      all: gridFilter(baseData, 0).length,
      passed: gridFilter(baseData, 1).length,
      softError: gridFilter(baseData, 2).length,
      hardError: gridFilter(baseData, 3).length,
    };
  }, [baseData, gridFilter, isDataReady]);

  // Fetch InvoiceOverview Data without Tab Filter
  const fetchInvoiceOverviewData = useCallback(async () => {
    // Check if all required data is loaded before fetching invoices
    if (!countryData || countryData.length === 0) {
      console.warn("Country data is not yet loaded. Skipping invoice fetch.");
      return; // Exit early if countryData is not available
    }
    if (!supplierData || supplierData.length === 0) {
      console.warn("Supplier data is not yet loaded. Skipping invoice fetch.");
      return; // Exit early if supplierData is not available
    }
    if (!entitiesData || entitiesData.length === 0) {
      console.warn("Entities data is not yet loaded. Skipping invoice fetch.");
      return; // Exit early if entitiesData is not available
    }
    if (!productData || productData.length === 0) {
      console.warn("Product data is not yet loaded. Skipping invoice fetch.");
      return; // Exit early if productData is not available
    }

    setLoading(true);
    try {
      const response = await axios.get("/api/v1/ocr/getOCRScannedInvoices");
      const fetchedInvoices = response.data;

      console.log("fetchedInvoices", fetchedInvoices);

      // Process fetched invoices
      const processedFetchedInvoices = fetchedInvoices.map((invoice) => {
        let processedErrorMessages = [];
        let processedErrorCodes = [];
        let processedSoftErrorDesc = [];

        // Process errorMessages (only for hard errors)
        if (invoice.errorMessages) {
          if (Array.isArray(invoice.errorMessages)) {
            // Assuming errorMessages array corresponds to hard errors
            processedErrorMessages = invoice.errorMessages;
          } else if (typeof invoice.errorMessages === "string") {
            // Split string into array, assuming each line is a hard error message
            processedErrorMessages = invoice.errorMessages.split(/\r?\n/).filter((msg) => msg.trim() !== "");
          }
        }

        // Process errorCodes (only hard errors)
        if (invoice.errorCodes) {
          if (typeof invoice.errorCodes === "string") {
            processedErrorCodes = invoice.errorCodes
              .split(",")
              .map((code) => parseInt(code.trim(), 10))
              .filter((code) => !isNaN(code) && hardErrorCodes.includes(code));
          } else if (Array.isArray(invoice.errorCodes)) {
            processedErrorCodes = invoice.errorCodes.map((code) => parseInt(code, 10)).filter((code) => hardErrorCodes.includes(code));
          }
        }

        invoice.currencyId = countryData.find((country) => country.countryCode === invoice.countryCodeSupplier)?.currency || "";

        // Compute soft errors based on invoiceDate
        const computedSoftErrors = computeSoftErrorsForInvoice(invoice, countryData, supplierData, entitiesData, productData);

        // Map soft error codes to descriptions
        processedSoftErrorDesc = computedSoftErrors.map(
          (code) => softErrorDescriptionsMap[code] || `Unknown soft error code: ${code}`
        );

        return {
          ...invoice,
          errorMessages: processedErrorMessages || [], // Ensure it's an array
          errorCodes: processedErrorCodes || [], // Ensure it's an array
          softErrors: computedSoftErrors || [], // Ensure it's an array
          softErrorDesc: processedSoftErrorDesc || [], // Renamed to avoid shadowing
        };
      });

      setFetchedInvoiceData(processedFetchedInvoices);
    } catch (error) {
      console.error("Error fetching InvoiceOverview data", error);
      setAlertSeverity("error");
      setAlertMessage("Failed to fetch invoice data.");
      setOpenAlert(true);
      setTimeout(() => setOpenAlert(false), 5000);
    } finally {
      setLoading(false);
    }
  }, [hardErrorCodes, countryData, supplierData, entitiesData, productData]);

  // Fetch InvoiceOverview Data when component mounts or when filters change
  useEffect(() => {
    fetchInvoiceOverviewData();
  }, [fetchInvoiceOverviewData]);

  // Handle Filter Changes
  const handleFilter = () => {
    fetchInvoiceOverviewData();
  };

  // Handle Reset Filters
  const handleResetFilter = () => {
    setSelectedCountry("any");
    setSelectedUser("any");
    setSelectedBatchId("any");
    setSelectedYear("any");
    setSelectedStartMonth("any");
    setSelectedEndMonth("any");
    setSelectedState("any");
    fetchInvoiceOverviewData();
  };

  // DataGrid Columns Configuration
  const defaultColumns = useMemo(
    () => [
      {
        field: "batchId",
        headerName: "Batch ID",
        flex: 1,
        hide: false,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: "batchRate",
        headerName: "Batch Rate",
        flex: 1,
        renderCell: (params) => {
          const { batchTotalDocuments, batchMatchingDocuments } = params.row;

          // Data Validation: Ensure values are numbers
          const total = typeof batchTotalDocuments === "number" ? batchTotalDocuments : 0;
          const matching = typeof batchMatchingDocuments === "number" ? batchMatchingDocuments : 0;

          const isEqual = matching === total;
          const displayText = `${matching} / ${total}`;

          return (
            <Tooltip title={displayText} arrow>
              <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={{ color: isEqual ? "inherit" : "#F08080" }}>{displayText}</span>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "customerNumber",
        headerName: "Customer",
        flex: 1,
        hide: false,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice Number",
        flex: 1.5,
        hide: false,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: "invoiceDate",
        headerName: "Invoice Date",
        flex: 1,
        type: "date",
        // hide: rightPaneSize !== 0,
        valueFormatter: (params) => (params.value ? moment(params.value).format("DD-MM-YYYY") : ""),
        renderCell: (params) => {
          const formattedDate = params.value ? moment(params.value).format("DD-MM-YYYY") : "";
          return (
            <Tooltip title={formattedDate} arrow>
              <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formattedDate}</Box>
            </Tooltip>
          );
        },
      },
      {
        field: "supplierName",
        headerName: "Supplier",
        flex: 3,
        hide: rightPaneSize !== 0,
        valueGetter: (params) => {
          if (!params.row.taxIdSupplier || !supplierData || supplierData.length === 0) {
            return "";
          }
          const countryCode = countryData?.find(
            (country) => country.countryCode && params.row.taxIdSupplier.startsWith(country.countryCode)
          );
          const vatIdToCheck = countryCode ? params.row.taxIdSupplier.slice(countryCode.countryCode.length) : params.row.taxIdSupplier;
          const supplier = supplierData.find((supplier) => supplier.supplierVATnr === vatIdToCheck);
          return supplier ? supplier.supplierName : "";
        },
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value || ""}</Box>
          </Tooltip>
        ),
      },
      {
        field: "countryCode",
        headerName: "Country",
        flex: 1,
        hide: rightPaneSize !== 0,
        valueGetter: (params) => {
          return params.row.countryCodeSupplier || "";
        },
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value || ""}</Box>
          </Tooltip>
        ),
      },
      {
        field: "currencyId",
        headerName: "Currency",
        flex: 1,
        hide: rightPaneSize !== 0,
        valueGetter: (params) => {
          if (currencyData && currencyData.length > 0) {
            const currency = currencyData.find((cur) => cur.currencyId === params.row.currencyId);
            return currency ? currency.isoCode : "";
          }
          return "";
        },
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: "totalExcludingTax",
        headerName: "Net Amount",
        flex: 1.2,
        hide: rightPaneSize !== 0,
        align: "right",
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === "") {
            return "";
          }
          const numValue = typeof params.value === "string" ? parseFloat(params.value.replace(",", ".")) : params.value;
          return isNaN(numValue) ? "" : numValue.toFixed(2).replace(".", ",");
        },
        renderCell: (params) => {
          const formattedValue =
            params.value === null || params.value === undefined || params.value === ""
              ? ""
              : (() => {
                  const numValue = typeof params.value === "string" ? parseFloat(params.value.replace(",", ".")) : params.value;
                  return isNaN(numValue) ? "" : numValue.toFixed(2).replace(".", ",");
                })();
          return (
            <Tooltip title={formattedValue || ""} arrow>
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "right",
                  paddingRight: "15px",
                }}
              >
                {formattedValue}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "totalTax",
        headerName: "VAT Amount",
        flex: 1.2,
        hide: rightPaneSize !== 0,
        align: "right",
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === "") {
            return "";
          }
          const numValue = typeof params.value === "string" ? parseFloat(params.value.replace(",", ".")) : params.value;
          return isNaN(numValue) ? "" : numValue.toFixed(2).replace(".", ",");
        },
        renderCell: (params) => {
          const formattedValue =
            params.value === null || params.value === undefined || params.value === ""
              ? ""
              : (() => {
                  const numValue = typeof params.value === "string" ? parseFloat(params.value.replace(",", ".")) : params.value;
                  return isNaN(numValue) ? "" : numValue.toFixed(2).replace(".", ",");
                })();
          return (
            <Tooltip title={formattedValue || ""} arrow>
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "right",
                  paddingRight: "15px",
                }}
              >
                {formattedValue}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "errorCodesCombined",
        headerName: "Error Codes",
        width: 100,
        renderCell: (params) => {
          const { errorCodes, softErrors } = params.row;

          const hasHardErrors = errorCodes && errorCodes.length > 0;
          const hasSoftErrors = softErrors && softErrors.length > 0;

          if (!hasHardErrors && !hasSoftErrors) {
            return "";
          }

          const hardErrorDisplay = hasHardErrors ? `${errorCodes.join(", ")}` : "";
          const softErrorDisplay = hasSoftErrors ? `${softErrors.join(", ")}` : "";

          const combinedDisplay = [hardErrorDisplay, softErrorDisplay].filter(Boolean).join(", ");

          return (
            <Tooltip title={combinedDisplay} arrow>
              <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{combinedDisplay}</Box>
            </Tooltip>
          );
        },
      },
      {
        field: "checkProgress",
        headerName: "Check Progress",
        width: 130,
        renderCell: (params) => {
          const { succesFullNrChecks, totalNrChecks, errorMessages, errorCodes, softErrors, softErrorDesc } = params.row;

          if (totalNrChecks === undefined || succesFullNrChecks === undefined || totalNrChecks === 0) {
            return "N/A";
          }

          const hasHardErrors = errorCodes && errorCodes.length > 0;
          const hasSoftErrors = softErrors && softErrors.length > 0;
          const softErrorCount = softErrorDesc.length;
          const hardErrorCount = errorCodes.filter((code) => hardErrorCodes.includes(code)).length;
          const adjustedTotalNrChecks = totalNrChecks + softErrorCount;
          const adjustedSuccesFullNrChecks = Math.max(adjustedTotalNrChecks - softErrorCount - hardErrorCount, 0);

          const finalTotalChecks = adjustedTotalNrChecks;
          const finalSuccessChecks = adjustedSuccesFullNrChecks;

          const tooltipContent = (
            <div>
              <div>Check Progress:</div>
              <ul style={{ paddingLeft: "16px", margin: 0 }}>
                {Array.isArray(errorMessages) &&
                  errorMessages.length > 0 &&
                  errorMessages.map((msg, index) => <li key={index}>{msg}</li>)}
                {Array.isArray(softErrorDesc) &&
                  softErrorDesc.length > 0 &&
                  softErrorDesc.map((desc, index) => <li key={`soft-${index}`}>{desc}</li>)}
              </ul>
            </div>
          );

          let bgColor;
          if (hasHardErrors) {
            bgColor = "#d32f2f";
          } else if (hasSoftErrors) {
            bgColor = "#ed6c02";
          } else {
            bgColor = "#2e7d32";
          }
          const rgbMatch = bgColor.match(/\d+/g);
          const [r, g, b] = rgbMatch ? rgbMatch.map(Number) : [0, 0, 0];
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          const textColor = brightness > 125 ? "black" : "white";

          return (
            <Tooltip title={tooltipContent} arrow>
              <Box
                sx={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  textAlign: "center",
                  backgroundColor: bgColor,
                  color: textColor,
                  cursor: hasHardErrors || hasSoftErrors ? "pointer" : "default",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {finalSuccessChecks} / {finalTotalChecks}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [hardErrorCodes, supplierData, countryData, currencyData, rightPaneSize]
  );

  // Definieer kolommen voor Hard Error Invoices tab
  const hardErrorColumns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: "batchId",
        headerName: "Batch ID",
        flex: 0.5,
        hide: false,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: "batchRate",
        headerName: "Batch Rate",
        flex: 0.5,
        renderCell: (params) => {
          const { batchTotalDocuments, batchMatchingDocuments } = params.row;

          // Data Validation: Ensure values are numbers
          const total = typeof batchTotalDocuments === "number" ? batchTotalDocuments : 0;
          const matching = typeof batchMatchingDocuments === "number" ? batchMatchingDocuments : 0;

          const isEqual = matching === total;
          const displayText = `${matching} / ${total}`;

          return (
            <Tooltip title={displayText} arrow>
              <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={{ color: isEqual ? "inherit" : "#F08080" }}>{displayText}</span>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "errorCodes",
        headerName: "Error Codes",
        flex: 1,
        valueGetter: (params) =>
          Array.isArray(params.row.errorCodes) ? [...params.row.errorCodes].sort((a, b) => a - b).join(",") : "",
        sortComparator: (v1, v2) => v1.localeCompare(v2),
        renderCell: (params) => {
          const errorCodes = Array.isArray(params.row.errorCodes) ? params.row.errorCodes.join(",") : "";
          return (
            <Tooltip title={errorCodes} arrow>
              <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{errorCodes}</Box>
            </Tooltip>
          );
        },
      },
      {
        field: "errorMessages",
        headerName: "Error Messages",
        flex: 2,
        valueGetter: (params) => (Array.isArray(params.row.errorMessages) ? params.row.errorMessages.join(" | ") : ""),
        sortComparator: (v1, v2) => v1.localeCompare(v2),
        renderCell: (params) => {
          const errorMessages = Array.isArray(params.row.errorMessages) ? params.row.errorMessages.join(" | ") : "";
          const tooltipContent = Array.isArray(params.row.errorMessages) ? (
            <div>
              {params.row.errorMessages.map((message, index) => (
                <div key={index} style={{ marginBottom: index < params.row.errorMessages.length - 1 ? "4px" : "0" }}>
                  {message}
                </div>
              ))}
            </div>
          ) : (
            ""
          );

          return (
            <Tooltip
              title={tooltipContent}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: "none",
                    whiteSpace: "pre-line",
                    bgcolor: "background.paper",
                    ...getThemeTextColor(theme),
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 1,
                  },
                },
              }}
            >
              <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{errorMessages}</Box>
            </Tooltip>
          );
        },
      },
      {
        field: "originalUploadFileName",
        headerName: "Original Upload File",
        flex: 2,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <Box sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.value}</Box>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const columns = useMemo(() => {
    if (activeTab === 3) {
      return hardErrorColumns;
    }
    return defaultColumns;
  }, [activeTab, defaultColumns, hardErrorColumns]);

  // Define the selectRow function
  const selectRow = useCallback(
    (row) => {
      setHighlightedRowId(row.id);
      setViewCustomerInvoiceDialog({
        isOpen: true,
        downloadFile: true,
        params: row,
      });
      setRightPaneSize(lastRightPaneSize);
    },
    [lastRightPaneSize]
  );

  // Update handleRowClick to use selectRow
  const handleRowClick = (params, event) => {
    event.defaultMuiPrevented = true;
    selectRow(params.row);
  };

  // Add selectionModel state
  const [selectionModel, setSelectionModel] = useState([]);

  // Helper function to wait for an element to be available in the DOM
  const waitForElement = useCallback((selector, maxRetries = 20, delay = 50) => {
    return new Promise((resolve) => {
      let attempts = 0;
      const checkElement = () => {
        const element = document.querySelector(selector);
        if (element || attempts >= maxRetries) {
          resolve(element);
        } else {
          attempts++;
          setTimeout(checkElement, delay);
        }
      };
      checkElement();
    });
  }, []);

  // Update handleCellKeyDown to handle arrow keys and +/-
  const handleCellKeyDown = useCallback(
    (params, event) => {
      const { softErrors } = params.row;
      const selectionAllowed =
        softErrors && softErrors.length > 0 ? softErrors.every((code) => allowedSoftErrorCodes.includes(code)) : true;

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault(); // Prevent default navigation

        // Get current visible rows
        const startIndex = page * pageSize;
        const endIndex = startIndex + pageSize;
        const visibleRows = filteredData.slice(startIndex, endIndex);

        // Find the index of the current row within the visible rows
        const rowIndexInVisibleRows = visibleRows.findIndex((row) => row.id === params.id);

        if (rowIndexInVisibleRows === -1) {
          // Row not found in visible rows, something is wrong
          return;
        }

        // Prevent moving focus to the header when on the top row of the first page
        if (event.key === "ArrowUp" && rowIndexInVisibleRows === 0 && page === 0) {
          // Prevent the default behavior
          event.preventDefault();

          // Stop propagation to ensure no other focus management takes over
          event.stopPropagation();

          // Refocus on the current cell to keep the focus within the grid
          const currentCellSelector = `[data-id="${params.id}"] [data-field="${params.field}"]`;

          waitForElement(currentCellSelector).then((currentCell) => {
            if (currentCell) {
              currentCell.focus();
            }
          });
          return; // Exit the function to prevent any further handling
        }

        // Handle ArrowUp at the top of the grid but not on the first page
        if (event.key === "ArrowUp" && rowIndexInVisibleRows === 0) {
          if (page > 0) {
            const newPage = page - 1;
            setPage(newPage);

            // Use requestAnimationFrame to ensure page change is processed first
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  const newStartIndex = newPage * pageSize;
                  const newVisibleRows = filteredData.slice(newStartIndex, newStartIndex + pageSize);
                  const lastRow = newVisibleRows[newVisibleRows.length - 1];

                  if (lastRow) {
                    const cellSelector = `[data-id="${lastRow.id}"] [data-field="${params.field}"]`;
                    waitForElement(cellSelector, 30, 100).then((cell) => {
                      if (cell) {
                        cell.focus();
                        selectRow(lastRow);
                      }
                    });
                  }
                }, 100);
              });
            });
          }
        }

        // Handle ArrowDown at the bottom of the grid to prevent focus from moving out of the grid
        else if (event.key === "ArrowDown" && rowIndexInVisibleRows === visibleRows.length - 1) {
          const totalPages = Math.ceil(filteredData.length / pageSize);
          if (page < totalPages - 1) {
            // Move to the first row of the next page
            const newPage = page + 1;
            setPage(newPage);

            // Use requestAnimationFrame to ensure page change is processed first
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  const newStartIndex = newPage * pageSize;
                  const newVisibleRows = filteredData.slice(newStartIndex, newStartIndex + pageSize);
                  const firstRow = newVisibleRows[0];

                  if (firstRow) {
                    waitForElement(`[data-id="${firstRow.id}"] [data-field="${params.field}"]`, 30, 100).then((cell) => {
                      if (cell) {
                        cell.focus();
                        selectRow(firstRow);
                      }
                    });
                  }
                }, 100);
              });
            });
          } else {
            // Prevent moving focus out of the grid if at the bottom row of the last page
            event.preventDefault();
          }
        }

        // Normal navigation within the page
        else {
          // Use requestAnimationFrame to ensure the browser has updated the focus
          requestAnimationFrame(() => {
            setTimeout(() => {
              const focusedCell = document.activeElement;
              const rowElement = focusedCell.closest("[data-id]");
              if (rowElement) {
                const rowId = rowElement.getAttribute("data-id");
                if (rowId) {
                  const newRow = filteredData.find((row) => row.id.toString() === rowId);
                  if (newRow) {
                    selectRow(newRow);
                  }
                }
              }
            }, 50);
          });
        }
      }

      // Other key handling (selection logic)
      else if (event.key === "+" && selectionAllowed) {
        const rowId = params.id;
        setSelectionModel((prevSelection) => (!prevSelection.includes(rowId) ? [...prevSelection, rowId] : prevSelection));
      } else if (event.key === "-" && selectionAllowed) {
        const rowId = params.id;
        setSelectionModel((prevSelection) =>
          prevSelection.includes(rowId) ? prevSelection.filter((id) => id !== rowId) : prevSelection
        );
      } else if (event.key === " " && selectionAllowed) {
        event.preventDefault();
        const rowId = params.id;
        setSelectionModel((prevSelection) =>
          prevSelection.includes(rowId) ? prevSelection.filter((id) => id !== rowId) : [...prevSelection, rowId]
        );
      }
    },
    [filteredData, selectRow, page, pageSize, allowedSoftErrorCodes, waitForElement]
  );

  // Handle Pane Change
  const handlePaneChange = (size) => {
    setRightPaneSize(size);
    if (size !== 0) {
      setLastRightPaneSize(size);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.ctrlKey || event.key !== "Enter") {
        return;
      }

      const hasModifierConflict = event.altKey || event.metaKey;
      if (hasModifierConflict) {
        return;
      }

      event.preventDefault();

      const currentSelectedId = highlightedRowId ?? viewCustomerInvoiceDialog.params?.id ?? null;
      const currentIndex = filteredData.findIndex((invoice) => invoice.id === currentSelectedId);

      if (event.shiftKey) {
        const prevIndex = currentIndex <= 0 ? filteredData.length - 1 : currentIndex - 1;
        const prevInvoice = filteredData[prevIndex];
        if (prevInvoice) {
          const prevPage = Math.floor(prevIndex / pageSize);
          if (prevPage !== page) {
            setPage(prevPage);
          }
          selectRow(prevInvoice);
        }
      } else {
        const nextIndex = currentIndex === -1 || currentIndex >= filteredData.length - 1 ? 0 : currentIndex + 1;
        const nextInvoice = filteredData[nextIndex];
        if (nextInvoice) {
          const nextPage = Math.floor(nextIndex / pageSize);
          if (nextPage !== page) {
            setPage(nextPage);
          }
          selectRow(nextInvoice);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [filteredData, highlightedRowId, page, pageSize, selectRow, viewCustomerInvoiceDialog.params?.id]);

  const [updateTimestamp, setUpdateTimestamp] = useState(null);
  // Refactored updateInvoiceInTable function
  const updateInvoiceInTable = useCallback(
    (updatedInvoice) => {
      console.log("updateInvoiceInTable - updatedInvoice:", updatedInvoice);

      // Recompute softErrors for the updated invoice
      const recomputedSoftErrors = computeSoftErrorsForInvoice(updatedInvoice, countryData, supplierData, entitiesData, productData);
      console.log("updateInvoiceInTable - recomputedSoftErrors:", recomputedSoftErrors);

      // Map soft error codes to descriptions
      const recomputedSoftErrorDesc = recomputedSoftErrors.map(
        (code) => softErrorDescriptionsMap[code] || `Unknown soft error code: ${code}`
      );

      // Update local invoice updates without affecting filters
      setLocalInvoiceUpdates((prevUpdates) => {
        console.log("updateInvoiceInTable - prevUpdates:", prevUpdates);

        // Check if this invoice is being updated from the Hard Error tab (tab 3)
        const isFromHardErrorTab = activeTab === 3;

        // Check if this invoice was previously a hard error invoice
        const wasHardError =
          isFromHardErrorTab || updatedInvoice.prevHardError === 1 || prevUpdates[updatedInvoice.id]?.prevHardError === 1;

        // Preserve fileId from previous updates if not provided in updatedInvoice
        const existingFileId = prevUpdates[updatedInvoice.id]?.fileId;
        const fileIdToKeep = updatedInvoice.fileId !== undefined ? updatedInvoice.fileId : existingFileId;

        const newUpdates = {
          ...prevUpdates,
          [updatedInvoice.id]: {
            ...updatedInvoice,
            // Preserve fileId if it exists in previous updates and not in updatedInvoice
            ...(fileIdToKeep !== undefined && { fileId: fileIdToKeep }),
            softErrors: recomputedSoftErrors || [],
            softErrorDesc: recomputedSoftErrorDesc || [],
            // Clear error fields and set succeeded to true if updating from Hard Error tab
            // Only set prevHardError when updating from tab 3, otherwise clear it if invoice no longer has hard errors
            ...(isFromHardErrorTab && {
              errorCodes: [],
              errorMessages: [],
              succeeded: true,
              prevHardError: 1,
            }),
            // If updating from other tabs (0, 1, 2), invoice should be succeeded (no hard errors)
            // Always set succeeded to true and clear hard errors when updating from non-hard-error tabs
            // Preserve prevHardError if it was previously 1
            ...(!isFromHardErrorTab && {
              errorCodes: [],
              errorMessages: [],
              succeeded: true,
              prevHardError: updatedInvoice.prevHardError === 1 || prevUpdates[updatedInvoice.id]?.prevHardError === 1 ? 1 : 0,
            }),
          },
        };
        console.log("updateInvoiceInTable - newUpdates:", newUpdates);
        return newUpdates;
      });

      setUpdateTimestamp(Date.now());
    },
    [countryData, supplierData, entitiesData, productData, activeTab]
  );

  // Handle User Select Value
  const handleUserSelectValue = (value) => {
    setSelectedUser(value);

    // Reset Batch ID to 'any' if Uploaded By is set to 'any'
    if (value === "any") {
      setSelectedBatchId("any");
    }
  };

  // Handle Batch ID Select Value
  const handleBatchIdValue = (value) => {
    setSelectedBatchId(value);
  };

  // Handle Year Select Value
  // const handleYearValue = (value) => {
  //   setSelectedYear(value);
  // };

  // Handle Start Month Select Value
  // const handleStartMonthValue = (value) => {
  //   setSelectedStartMonth(value);
  // };

  // Handle End Month Select Value
  // const handleEndMonthValue = (value) => {
  //   setSelectedEndMonth(value);
  // };

  // Handle State Select Value
  // const handleStateValue = (value) => {
  //   setSelectedState(value);
  // };

  const extractVATNumber = (taxId, countryArr) => {
    if (!taxId) return "";
    const sortedCountries = [...countryArr].sort((a, b) => b.countryCode.length - a.countryCode.length);
    const matchedCountry = sortedCountries.find((country) => taxId.toUpperCase().startsWith(country.countryCode.toUpperCase()));
    return matchedCountry ? taxId.slice(matchedCountry.countryCode.length) : taxId;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, "0"); // Months are zero-based
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to validate invoice before upload
  const validateInvoiceForUpload = useCallback(
    (invoice) => {
      const missingFields = [];

      // Validate required invoice fields
      if (!invoice.invoiceNumber || invoice.invoiceNumber.trim() === "") {
        missingFields.push("Invoice Number");
      }

      if (!invoice.invoiceDate) {
        missingFields.push("Invoice Date");
      } else {
        const date = new Date(invoice.invoiceDate);
        if (
          isNaN(date.getTime()) ||
          invoice.invoiceDate === "0000-00-00" ||
          invoice.invoiceDate === "01-01-1970" ||
          date.getFullYear() === 1970
        ) {
          missingFields.push("Invoice Date (invalid date)");
        }
      }

      if (!invoice.totalExcludingTax && invoice.totalExcludingTax !== 0) {
        missingFields.push("Total Excluding Tax");
      }

      if (!invoice.totalTax && invoice.totalTax !== 0) {
        missingFields.push("Total Tax");
      }

      if (!invoice.productId) {
        missingFields.push("Product ID");
      }

      // if (!invoice.newFileName) {
      //   missingFields.push("File Name");
      // }

      // if (!invoice.modelUsed) {
      //   missingFields.push("Model Used");
      // }

      // Validate entity
      const entityId = entitiesData.find((entity) => entity.vat === invoice.taxIdCustomer && entity.typeRelation === 1)?.entityId;
      if (!entityId) {
        missingFields.push("Customer Entity (not found)");
      }

      // Validate supplier
      if (!invoice.taxIdSupplier) {
        missingFields.push("Supplier VAT ID");
      } else {
        const fullTaxIdSupplier = invoice.taxIdSupplier;
        const extractedVATnr = extractVATNumber(fullTaxIdSupplier, countryData);
        const supplier = supplierData.find((supplier) => supplier.supplierVATnr === extractedVATnr);
        if (!supplier || !supplier.supplierId) {
          missingFields.push("Supplier (not found)");
        }
      }

      // Validate country and currency
      const fullTaxIdSupplier = invoice.taxIdSupplier;
      const extractedVATnr = extractVATNumber(fullTaxIdSupplier, countryData);
      const supplier = supplierData.find((supplier) => supplier.supplierVATnr === extractedVATnr);
      const invoiceCountryTemp = invoice.countryCode || supplier?.countryCode;
      const invoiceCountry = countryData.find((country) => country.countryId === invoiceCountryTemp);

      if (!invoiceCountry) {
        missingFields.push("Country Code (not found)");
      } else if (!invoiceCountry.currency) {
        missingFields.push("Currency (not found for country)");
      }

      return {
        isValid: missingFields.length === 0,
        missingFields: missingFields,
      };
    },
    [entitiesData, supplierData, countryData]
  );

  // Handle Upload Invoices
  const handleUploadInvoices = useCallback(async () => {
    try {
      // Retrieve selected invoices based on selectionModel
      const selectedInvoices = filteredData.filter((invoice) => selectionModel.includes(invoice.id));

      console.log("Selected Invoices for Upload:", selectedInvoices);

      if (selectedInvoices.length === 0) {
        setAlertSeverity("warning");
        setAlertMessage("No invoices selected for upload.");
        setOpenAlert(true);
        setTimeout(() => setOpenAlert(false), 5000);
        return;
      }

      // Open the modal immediately so user can see progress
      setIsUploadModalOpen(true);
      setUploadProgress(0); // Reset progress
      setUploadResults([]); // Reset results

      const results = [];
      for (let i = 0; i < selectedInvoices.length; i++) {
        const invoice = selectedInvoices[i];

        try {
          // Validate invoice before attempting upload
          const validation = validateInvoiceForUpload(invoice);
          if (!validation.isValid) {
            const errorMessage = `Missing required fields: ${validation.missingFields.join(", ")}`;
            results.push({
              customerNumber: invoice.customerNumber,
              invoiceNumber: invoice.invoiceNumber || "N/A",
              status: "Failed",
              error: errorMessage,
            });

            // Update progress after validation failure
            const progress = Math.round(((i + 1) / selectedInvoices.length) * 100);
            setUploadProgress(progress);
            continue; // Skip to next invoice
          }

          // Prepare the payload based on CustomerInvoicesCreateDto
          let entityId = entitiesData.find((entity) => entity.vat === invoice.taxIdCustomer && entity.typeRelation === 1)?.entityId; // entity type controlleren
          const fullTaxIdSupplier = invoice.taxIdSupplier;
          const extractedVATnr = extractVATNumber(fullTaxIdSupplier, countryData);
          let supplier = supplierData.find((supplier) => supplier.supplierVATnr === extractedVATnr);

          // Validate supplier exists
          if (!supplier || typeof supplier !== "object") {
            throw new Error(`Supplier not found for VAT number: ${extractedVATnr}`);
          }

          let invoiceCountryTemp = invoice.countryCode || supplier.countryCode;
          let invoiceCountry = countryData.find((country) => country.countryId === invoiceCountryTemp);

          // Validate invoiceCountry exists
          if (!invoiceCountry) {
            throw new Error(`Country not found for country ID: ${invoiceCountryTemp}`);
          }

          let invoiceCurrency = invoiceCountry.currency;
          let modelUsed = invoice.modelUsed;
          let fileName = invoice.newFileName;
          let hardError = invoice.prevHardError ?? 0;
          let fileIdFromAttachment = invoice.fileId; // Check if file was added via dropzone

          // Step 1: Download the existing file
          let downloadedFile;
          let fileSize;

          if (fileIdFromAttachment) {
            // File was added via dropzone, download from regular blob storage
            try {
              const downloadResponse = await axios({
                url: `/api/v1/azureblobstorage/${fileIdFromAttachment}`,
                method: "GET",
                responseType: "blob",
              });
              downloadedFile = downloadResponse.data;
              fileSize = downloadedFile.size;
            } catch (error) {
              console.error(`Failed to download file from regular blob storage:`, error);
              throw new Error(`Failed to download file: ${error.message}`);
            }
          } else if (fileName && fileName !== "" && fileName !== null) {
            // Original file from OCR blob storage
            try {
              const downloadResponse = await axios({
                url: "/api/v1/azureocrblobstorage/" + fileName + "/" + modelUsed + "/" + hardError,
                method: "GET",
                responseType: "blob",
              });
              downloadedFile = downloadResponse.data;
              fileSize = downloadedFile.size;
            } catch (error) {
              console.error(`Failed to download file from OCR blob storage:`, error);
              throw new Error(`Failed to download file: ${error.message}`);
            }
          } else {
            throw new Error(`No file available for invoice ${invoice.invoiceNumber || invoice.id}`);
          }

          // Step 2: Use existing fileId if available, otherwise create new one
          let fileId;
          if (fileIdFromAttachment) {
            // File was already uploaded via dropzone, use existing fileId
            fileId = fileIdFromAttachment;
          } else {
            // Create new file metadata and upload
            const fileInfo = {
              documentType: 1,
              creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
              createdBy: uploadedBy,
              fileName: invoice.newFileName || `${invoice.invoiceNumber}.pdf`,
              fileSize: downloadedFile.size,
              mimeType: "application/pdf",
            };

            // Step 3: Get fileId by posting fileInfo
            const fileResponse = await axios.post("/api/v1/file", fileInfo);
            fileId = fileResponse.data.fileId;

            // Step 4: Upload the file with the new filename {fileId}.pdf
            const formData = new FormData();
            formData.append("file", downloadedFile, fileId);

            // Upload to blob storage
            const uploadResponse = await axios.post("/api/v1/azureblobstorage", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });

            if (uploadResponse.status !== 200 && uploadResponse.status !== 201) {
              throw new Error(`Failed to upload file for invoice ${invoice.invoiceNumber}`);
            }
          }

          // Step 5: Update payload with fileId
          // console.log(invoice);
          const payload = {
            entityId: entityId,
            invoiceNumber: invoice.invoiceNumber.trim(),
            invoiceDate: formatDate(invoice.invoiceDate), // Ensure correct date format
            supplierId: supplier.supplierId,
            countryCode: invoice.countryCode ?? supplier.countryCode,
            claimProductTypeId: invoice.productId,
            CurrencyId: invoice.currencyId ?? invoiceCurrency,
            invoiceGrossAmount: invoice.totalExcludingTax,
            invoiceVATAmountLocalCurrency: invoice.totalTax,
            invoiceVATAmountEUR: 0,
            dataEntryDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
            dataEntryOperator: "System",
            invoiceStateId: invoice.invoiceStateId ?? 3,
            preFinanceId: invoice.preFinanceId ?? 0, // Default to 0 if undefined
            fileId: fileId, // Updated with the obtained fileId
          };

          // Step 6: Insert into the database
          const response = await axios.post("/api/v1/customerInvoices", payload);
          // console.log(`Invoice ${invoice.invoiceNumber} uploaded successfully:`, response.data);

          // Step 7: Set ProcessedByDWS
          await axios.put("/api/v1/ocr/setProcessedByDWS", {
            id: invoice.id,
            processedByDWS: true,
          });

          // Step 8: Remove the processed invoice from the fetchedInvoiceData
          setFetchedInvoiceData((prevData) => prevData.filter((inv) => inv.id !== invoice.id));

          results.push({
            customerNumber: invoice.customerNumber,
            invoiceNumber: invoice.invoiceNumber,
            status: "Success",
            data: response.data,
          });
        } catch (error) {
          // Check for specific "duplicate key" error
          const errorMessage = error.response?.data || error.message;
          // console.log("Error:", error);
          // console.log("Error Message:", errorMessage);
          const displayError = errorMessage.includes("Cannot insert duplicate key row in object")
            ? "Possible duplicate"
            : errorMessage;
          // console.log("Display Error:", displayError);
          // Failure result with custom error message if duplicate key error
          results.push({
            customerNumber: invoice.customerNumber,
            invoiceNumber: invoice.invoiceNumber || "N/A",
            status: "Failed",
            error: displayError,
          });
        }

        // Update progress after each upload
        const progress = Math.round(((i + 1) / selectedInvoices.length) * 100);
        setUploadProgress(progress); // Update modal progress
      }

      // Ensure progress is always 100% at the end
      setUploadProgress(100);

      // Prepare feedback messages
      const successCount = results.filter((res) => res.status === "Success").length;
      const failureCount = results.filter((res) => res.status === "Failed").length;

      // Set alert message
      if (failureCount === 0) {
        setAlertSeverity("success");
        setAlertMessage(`${successCount} invoice(s) uploaded successfully.`);
      } else if (successCount === 0) {
        setAlertSeverity("error");
        setAlertMessage(`Failed to upload ${failureCount} invoice(s).`);
      } else {
        setAlertSeverity("warning");
        setAlertMessage(`Uploaded ${successCount} invoice(s) successfully. Failed to upload ${failureCount} invoice(s).`);
      }

      // Set results - modal is already open, just update the results
      setUploadResults(results);

      // Show alert message after upload completes
      try {
        setOpenAlert(true);
        setTimeout(() => setOpenAlert(false), 7000);
      } catch (error) {
        console.error("Error showing alert:", error);
      }

      // Optional: Refresh the data to reflect uploaded invoices
      fetchInvoiceOverviewData();
    } catch (error) {
      // Catch any unhandled errors that occur outside the loop
      console.error("Unexpected error in handleUploadInvoices:", error);

      // Ensure progress is set to 100% even on error
      setUploadProgress(100);

      // Add error to results if we have any invoices
      const errorResult = {
        customerNumber: "N/A",
        invoiceNumber: "N/A",
        status: "Failed",
        error: `Unexpected error: ${error.message || "Unknown error occurred"}`,
      };

      setUploadResults([errorResult]);

      // Set alert message
      setAlertSeverity("error");
      setAlertMessage(`An unexpected error occurred during upload: ${error.message || "Unknown error"}`);

      // Ensure modal is opened even on error (in case it wasn't opened at the start)
      try {
        setIsUploadModalOpen(true);
        setOpenAlert(true);
        setTimeout(() => setOpenAlert(false), 7000);
      } catch (modalError) {
        console.error("Error opening modal:", modalError);
        // Last resort: try to open modal without alert
        setIsUploadModalOpen(true);
      }
    }
  }, [
    filteredData,
    selectionModel,
    fetchInvoiceOverviewData,
    entitiesData,
    supplierData,
    countryData,
    uploadedBy,
    validateInvoiceForUpload,
  ]);

  // Define isRowSelectable function
  const isRowSelectable = useCallback(
    (params) => {
      if (selectionEnabled) return true; // All rows selectable when enabled

      if (activeTab === 3) {
        return true; // All rows selectable when Tab 3 is active
      }

      const { softErrors } = params.row;

      if (softErrors && softErrors.length > 0) {
        // Only selectable if all softErrors are allowed
        return softErrors.every((code) => allowedSoftErrorCodes.includes(code));
      }

      // If no softErrors, row is selectable
      return true;
    },
    [activeTab, allowedSoftErrorCodes, selectionEnabled]
  );

  // Add this useEffect after your existing useEffect hooks
  useEffect(() => {
    // Skip filtering if selection is enabled OR if we're on the hard error invoices tab (tab 3)
    if (!selectionEnabled && activeTab !== 3) {
      // When selection is disabled, filter selectionModel to only allowed rows
      const newSelection = selectionModel.filter((id) => {
        const row = filteredData.find((invoice) => invoice.id === id);
        if (row && row.softErrors) {
          return row.softErrors.every((code) => allowedSoftErrorCodes.includes(code));
        }
        return true;
      });

      // Only update if the selection actually changed to avoid unnecessary re-renders
      if (newSelection.length !== selectionModel.length || !newSelection.every((id) => selectionModel.includes(id))) {
        setSelectionModel(newSelection);
      }
    }
  }, [selectionEnabled, filteredData, selectionModel, allowedSoftErrorCodes, activeTab]);

  const handleToggleSelectionEnabled = useCallback(() => {
    setSelectionEnabled((prev) => !prev);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // const handleDeleteInvoices = useCallback(async () => {
  //   // Bevestiging voor de gebruiker
  //   if (!window.confirm("Are you sure you want to delete the selected invoices? This action cannot be undone.")) {
  //     return;
  //   }

  //   // Haal de geselecteerde facturen op
  //   const selectedInvoices = filteredData.filter((invoice) => selectionModel.includes(invoice.id));

  //   if (selectedInvoices.length === 0) {
  //     setAlertSeverity("warning");
  //     setAlertMessage("No invoices selected for deletion.");
  //     setOpenAlert(true);
  //     setTimeout(() => setOpenAlert(false), 5000);
  //     return;
  //   }

  //   // console.log("Deleting invoices:", selectedInvoices);
  //   // return;

  //   setDeleteInProgress(true);
  //   const results = [];

  //   for (const invoice of selectedInvoices) {
  //     try {
  //       await axios.put(`/api/v1/ocr/setRemovedByDWS/${invoice.id}`); // Pas de API endpoint aan indien nodig
  //       results.push({
  //         invoiceNumber: invoice.invoiceNumber,
  //         status: "Success",
  //       });
  //     } catch (error) {
  //       console.error(`Failed to delete invoice ${invoice.invoiceNumber}:`, error);
  //       results.push({
  //         invoiceNumber: invoice.invoiceNumber,
  //         status: "Failed",
  //         error: error.response?.data?.message || error.message,
  //       });
  //     }
  //   }

  //   setDeleteInProgress(false);
  //   // setDeleteResults(results); // Optioneel: Om individuele delete resultaten weer te geven

  //   // Feedback berichten
  //   const successCount = results.filter((res) => res.status === "Success").length;
  //   const failureCount = results.filter((res) => res.status === "Failed").length;

  //   if (failureCount === 0) {
  //     setAlertSeverity("success");
  //     setAlertMessage(`${successCount} invoice(s) deleted successfully.`);
  //   } else if (successCount === 0) {
  //     setAlertSeverity("error");
  //     setAlertMessage(`Failed to delete ${failureCount} invoice(s).`);
  //   } else {
  //     setAlertSeverity("warning");
  //     setAlertMessage(`Deleted ${successCount} invoice(s) successfully. Failed to delete ${failureCount} invoice(s).`);
  //   }
  //   setOpenAlert(true);
  //   setTimeout(() => setOpenAlert(false), 7000);

  //   // Optioneel: Vernieuw de data om verwijderde facturen te reflecteren
  //   fetchInvoiceOverviewData();
  // }, [filteredData, selectionModel, fetchInvoiceOverviewData]);

  const updateBatchIdOptions = useCallback(() => {
    let batchIds = [];
    if (selectedUser === "any") {
      batchIds = allInvoiceData.map((inv) => inv.batchId);
    } else {
      batchIds = allInvoiceData.filter((inv) => inv.uploadedBy === selectedUser).map((inv) => inv.batchId);
    }
    const uniqueBatchIds = Array.from(new Set(batchIds)).sort((a, b) => a - b);
    const newBatchIdOptions = uniqueBatchIds.map((id) => ({ key: id, value: id }));

    setBatchIdOptions(newBatchIdOptions);

    // Check if the currently selectedBatchId is still in the new options
    const isSelectedBatchIdValid = newBatchIdOptions.some((option) => option.value === selectedBatchId);

    if (!isSelectedBatchIdValid) {
      setSelectedBatchId("any");
    }
    // Else, retain the current selectedBatchId
  }, [selectedUser, allInvoiceData, selectedBatchId]);

  // Use useEffect to call updateBatchIdOptions whenever selectedUser or allInvoiceData changes
  useEffect(() => {
    updateBatchIdOptions();
  }, [updateBatchIdOptions]);

  const [setProcessedByDwsInProgress, setSetProcessedByDwsInProgress] = useState(false);

  const handleSetProcessedByDWS = useCallback(async () => {
    const selectedInvoices = filteredData.filter((invoice) => selectionModel.includes(invoice.id));

    if (selectedInvoices.length === 0) {
      setAlertSeverity("warning");
      setAlertMessage("No invoices selected for processing.");
      setOpenAlert(true);
      setTimeout(() => setOpenAlert(false), 5000);
      return;
    }

    setSetProcessedByDwsInProgress(true);
    const results = [];

    for (const invoice of selectedInvoices) {
      try {
        await axios.put("/api/v1/ocr/setProcessedByDWS", {
          id: invoice.id,
          processedByDWS: true,
        });
        results.push({
          invoiceNumber: invoice.invoiceNumber,
          status: "Success",
        });
      } catch (error) {
        console.error(`Failed to set ProcessedByDWS for invoice ${invoice.invoiceNumber}:`, error);
        results.push({
          invoiceNumber: invoice.invoiceNumber,
          status: "Failed",
          error: error.response?.data?.message || error.message,
        });
      }
    }

    setSetProcessedByDwsInProgress(false);

    const successCount = results.filter((res) => res.status === "Success").length;
    const failureCount = results.filter((res) => res.status === "Failed").length;

    if (failureCount === 0) {
      setAlertSeverity("success");
      setAlertMessage(`Processed ${successCount} invoice(s) successfully.`);
    } else if (successCount === 0) {
      setAlertSeverity("error");
      setAlertMessage(`Failed to process ${failureCount} invoice(s).`);
    } else {
      setAlertSeverity("warning");
      setAlertMessage(`Processed ${successCount} invoice(s) successfully. Failed to process ${failureCount} invoice(s).`);
    }
    setOpenAlert(true);
    setTimeout(() => setOpenAlert(false), 7000);

    // Optional: Refresh the data to reflect changes
    // fetchInvoiceOverviewData();
    setFetchedInvoiceData((prevData) => prevData.filter((invoice) => !selectedInvoices.some((sel) => sel.id === invoice.id)));
  }, [filteredData, selectionModel]);

  // console.log("filteredData", filteredData);

  return (
    <React.Fragment>
      <Helmet title="Review OCR Invoices" />
      <Snackbar
        open={openAlert}
        autoHideDuration={5000}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenAlert(false)} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label="Invoice Tabs">
        <Tab
          label={
            <Box display="flex" alignItems="left" position="relative">
              All Invoices
              <Badge
                badgeContent={tabCounts.all}
                color="primary"
                max={Infinity} // Set a very high value to prevent truncation
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  ml: 3,
                  transform: "translateY(-20%)", // Maintain vertical adjustment
                }}
              />
            </Box>
          }
          sx={{ typography: "h6", fontWeight: "bold" }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="left" position="relative">
              Passed Invoices
              <Badge
                badgeContent={tabCounts.passed}
                color="success"
                max={Infinity} // Set a very high value to prevent truncation
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  ml: 3,
                  transform: "translateY(-20%)",
                }}
              />
            </Box>
          }
          sx={{ typography: "h6", fontWeight: "bold" }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="left" position="relative">
              Soft Error Invoices
              <Badge
                badgeContent={tabCounts.softError}
                color="warning"
                max={Infinity} // Set a very high value to prevent truncation
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  ml: 3,
                  transform: "translateY(-20%)",
                }}
              />
            </Box>
          }
          sx={{ typography: "h6", fontWeight: "bold" }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="left" position="relative">
              Hard Error Invoices
              <Badge
                badgeContent={tabCounts.hardError}
                color="error"
                max={Infinity} // Set a very high value to prevent truncation
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  ml: 3,
                  transform: "translateY(-20%)",
                }}
              />
            </Box>
          }
          sx={{ typography: "h6", fontWeight: "bold" }}
        />
      </Tabs>

      <Divider my={6} />
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "calc(100vh - 280px)", // Adjusted to leave room for footer and other elements
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PanelGroup direction="horizontal" style={{ height: "100%" }} onUpdate={handlePaneChange} defaultSizes={[70, 30]}>
          <Panel minSize={20} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <CardContent sx={{ paddingBottom: 0, flexShrink: 0 }}>
                {/*<Typography variant="h6" gutterBottom>
                    Check Automated Invoices
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Invoices that are processed by the OCR system. Please select your name and batch ID to filter the invoices. After
                    viewing and updating the invoices, you can upload them to DWS using the button Upload selected invoices to DWS. You
                    can view the invoice details by clicking on the row.
                  </Typography>*/}
                {/* Add your filter components here if any */}
              </CardContent>
              {/* <Grid container spacing={0} margin={0} padding={0} style={{ overflow: "auto", flex: 1 }}> */}
              <Grid container spacing={5} sx={{ pr: 2, mb: 2 }}>
                <Grid item xs={2}>
                  <SelectWrapper
                    name="uploadedBy"
                    labelColor="#f64435"
                    label="Uploaded By"
                    options={[
                      { key: "any", value: "Any" },
                      ...(Array.isArray(dwsUserData)
                        ? dwsUserData.map((user) => ({
                            key: user.id,
                            value: user.firstname,
                          }))
                        : []),
                    ]}
                    value={selectedUser}
                    handleChange={handleUserSelectValue}
                  />
                </Grid>
                <Grid item xs={2}>
                  <SelectWrapper
                    name="batchId"
                    label="Batch ID"
                    options={[{ key: "any", value: "Any" }, ...batchIdOptions]}
                    value={selectedBatchId}
                    handleChange={handleBatchIdValue}
                    disabled={selectedUser === "any"} // Disable if no user is selected
                  />
                </Grid>
                <Grid item xs={2}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button onClick={handleFilter} sx={{ pl: 6, pr: 6, mt: 4 }} variant="contained">
                      Filter
                    </Button>
                    <Button onClick={handleResetFilter} sx={{ pl: 6, pr: 6, mt: 4 }} variant="outlined">
                      Reset
                    </Button>
                    <Button
                      onClick={handleToggleSelectionEnabled}
                      sx={{
                        pl: 6,
                        pr: 6,
                        mt: 4,
                        backgroundColor: selectionEnabled ? "error.light" : "inherit", // Light red background when enabled
                        color: selectionEnabled ? "white" : "primary.main", // White text when enabled, blue when not
                        "&:hover": {
                          backgroundColor: selectionEnabled ? "error.main" : "inherit", // Darker red on hover when enabled
                        },
                        minWidth: "auto", // Adjust width to fit the text
                        whiteSpace: "nowrap", // Ensure text stays on one line
                        transition: "background-color 0.3s ease", // Smooth transition for background color
                      }}
                      variant="outlined"
                    >
                      {selectionEnabled ? "Disable Selection" : "Enable All Selection"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} mt={1} pt={1}>
                {!isDataReady || loading || !isDataProcessed ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "640px",
                      width: "100%",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <DataGrid
                    key={selectionEnabled ? "ocr-all-selection-enabled" : "ocr-validated-selection-only"}
                    getRowId={(row) => row.id} // Ensure this matches your unique row identifier
                    disableSelectionOnClick={true}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                      toolbar: {
                        csvOptions: { disableToolbarButton: false },
                        printOptions: { disableToolbarButton: false },
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 250 },
                      },
                    }}
                    density="compact"
                    checkboxSelection
                    rows={filteredData}
                    columns={columns}
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    pageSize={pageSize}
                    loading={loading}
                    onRowClick={handleRowClick}
                    getRowClassName={(params) => (params.row.id === highlightedRowId ? "highlighted-row" : "")}
                    onCellKeyDown={handleCellKeyDown}
                    selectionModel={selectionModel}
                    onSelectionModelChange={(newSelection) => {
                      setSelectionModel(newSelection);
                    }}
                    isRowSelectable={isRowSelectable}
                    sx={{
                      height: "640px",
                      width: "100%",
                      borderRadius: 1,
                      backgroundColor: "background.paper",
                      boxShadow: 2,
                      "& .MuiDataGrid-cell:hover": {
                        color: "primary.main",
                      },
                      "& .highlighted-row": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.15)",
                        },
                      },
                      "& .disabled-row": {
                        opacity: 0.5,
                        pointerEvents: "none",
                      },
                    }}
                  />
                )}

                {/* Upload Button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginRight: "20px",
                    marginTop: "10px",
                  }}
                >
                  {activeTab === 3 ? (
                    <Button
                      type="button"
                      onClick={() => setIsConfirmDialogOpen(true)}
                      variant="contained"
                      color="error" // Gebruik een andere kleur voor delete actie
                      mt={3}
                      my={2}
                      mr={4}
                      disabled={setProcessedByDwsInProgress} // Disable button tijdens delete operatie
                    >
                      {setProcessedByDwsInProgress ? (
                        <Box display="flex" alignItems="center">
                          Deleting...
                          <CircularProgress size={20} sx={{ ml: 1 }} />
                        </Box>
                      ) : (
                        "Delete selected invoices from the system"
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        onClick={() => setIsConfirmDialogOpen(true)}
                        variant="contained"
                        color="error"
                        sx={{
                          marginRight: "20px",
                        }}
                        mt={3}
                        my={2}
                        disabled={setProcessedByDwsInProgress}
                      >
                        {setProcessedByDwsInProgress ? (
                          <Box display="flex" alignItems="center">
                            Processing...
                            <CircularProgress size={20} sx={{ ml: 1 }} />
                          </Box>
                        ) : (
                          "Delete selected invoices from the system"
                        )}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleUploadInvoices}
                        variant="contained"
                        color="primary"
                        mt={3}
                        my={2}
                        mr={2}
                        disabled={uploadInProgress}
                      >
                        {uploadInProgress ? (
                          <Box display="flex" alignItems="center">
                            Uploading...
                            <CircularProgress size={20} sx={{ ml: 1 }} />
                          </Box>
                        ) : (
                          "Upload selected invoices to DWS"
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </Grid>
              {/* </Grid> */}
            </Card>
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle
            style={{
              width: "5px",
              cursor: "col-resize",
              backgroundColor: "#ddd",
              borderLeft: "1px solid #aaa",
            }}
          />

          {/* Right Panel */}
          <Panel minSize={0} style={{ overflow: "auto", display: rightPaneSize === 0 ? "none" : "block" }}>
            {rightPaneSize !== 0 && (
              <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
                <Card style={{ height: "100%", position: "relative", overflow: "auto" }}>
                  <CardContent style={{ height: "100%", paddingBottom: 16, overflow: "auto" }}>
                    <IconButton
                      onClick={() => {
                        setRightPaneSize(0);
                        setHighlightedRowId(null);
                        setViewCustomerInvoiceDialog({
                          ...viewCustomerInvoiceDialog,
                          isOpen: false,
                        });
                      }}
                      style={{ position: "absolute", top: 8, right: 8 }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <ViewCheckAutomatedInvoiceDialog
                      viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
                      setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
                      countryArr={countryData}
                      invoiceStateArr={invoiceStateData}
                      supplierArr={supplierData}
                      currencyArr={currencyData}
                      productTypeArr={productData}
                      entitiesArr={entitiesData}
                      refreshGrid={fetchInvoiceOverviewData}
                      updateInvoice={updateInvoiceInTable}
                      id="dialog-content" // Ensure the dialog has this id for focus management
                      activeTab={activeTab}
                      updateTimestamp={updateTimestamp}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </Panel>
        </PanelGroup>

        <UploadStatusModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          progress={uploadProgress}
          results={uploadResults}
        />
      </Box>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to remove the invoices from the list permantly?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSetProcessedByDWS();
              setIsConfirmDialogOpen(false);
            }}
            color="warning"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default InvoiceOverviewComponent;
